// Copyright (C) 2019-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import Select from 'antd/lib/select';
import { SelectValue } from 'antd/lib/select';

import { getCore } from 'cvat-core-wrapper';

const core = getCore();

type Props = {
    projectId: number | null;
    value: number[] | null; // Array to hold multiple reference IDs
    onSelect: (ids: number[] | null) => void; // Handle multiple reference IDs
    filter?: (value: Reference, index: number, array: Reference[]) => unknown;
};

type Reference = {
    id: number;
    name: string;
};

export default function ReferenceSearchField(props: Props): JSX.Element {
    const { value, filter, onSelect, projectId } = props;
    const [searchPhrase, setSearchPhrase] = useState('');

    const [references, setReferences] = useState<Reference[]>([]);

    const handleSearch = (searchValue: string): void => {
        core.references.searchNames(searchValue, 10, projectId).then((result: Reference[]) => {
            if (result) {
                setReferences(result);
            }
        });
        setSearchPhrase(searchValue);
        onSelect([]); // Clear selected references when searching
    };

    const handleFocus = (open: boolean): void => {
        if (!references.length && open) {
            core.references.searchNames(null, 10, projectId).then((result: Reference[]) => {
                let referencesResponse = result;
                if (typeof filter === 'function') {
                    referencesResponse = referencesResponse.filter(filter);
                }
                if (referencesResponse) {
                    setReferences(referencesResponse);
                }
            });
        }
        if (!open && !value && searchPhrase) {
            setSearchPhrase('');
        }
    };

    const handleSelect = (_value: SelectValue): void => {
        const selectedReferenceIds = (_value as string[]).map(Number); // Convert selected reference IDs to numbers
        const selectedReferenceNames = references.filter((t) => selectedReferenceIds.includes(t.id)).map((t) => t.name);
        setSearchPhrase(selectedReferenceNames.join(', ')); // Display selected reference names
        onSelect(selectedReferenceIds.length ? selectedReferenceIds : null); // Update selected reference IDs
    };


    useEffect(() => {
        if (value && value.length) {
            const missingReferences = value.filter((id) => !references.some((t) => t.id === id));
            if (missingReferences.length) {
                core.references.get({ id: missingReferences }).then((result: Reference[]) => {
                    const newReferences = result.map((reference) => ({
                        id: reference.id,
                        name: reference.name,
                    }));
                    setReferences([...references, ...newReferences]);
                    const selectedReferenceNames = references.filter((t) => value.includes(t.id)).map((t) => t.name);
                    setSearchPhrase(selectedReferenceNames.join(', '));
                });
            }
        } else {
            setSearchPhrase('');
        }
    }, [value]);

    useEffect(() => {
        setReferences([]);
    }, [filter]);

    useEffect(() => {
        setSearchPhrase('');
        onSelect([]);
        handleSearch('');
    }, [projectId]);

    return (
        <Select
            mode="multiple"
            value={value?.map(String)}
            placeholder="Select references"
            onSearch={handleSearch}
            onChange={handleSelect}
            className="cvat-project-search-field"
            onDropdownVisibleChange={handleFocus}
            options={references.map((proj) => ({
                value: proj.id.toString(),
                label: proj.name,
            }))}
        />
    );
}
